
import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { FAQItem } from '../types';

interface FAQItemLegacy {
    question: string;
    answer: string | string[];
}

const defaultFaqs: FAQItemLegacy[] = [
    {
        question: "1. ABOUT AUTOMAX CO. LTD",
        answer: "Automax Co. Ltd is an authorized dealer for Shacman heavy-duty trucks and CIMC trailers in Tanzania. We supply brand-new trucks, tippers, trailers, fuel tankers, LPG tankers, and lowbeds to logistics, construction, and transport operators across East & Central Africa."
    },
    {
        question: "2. PRODUCT SPECIFICATIONS",
        answer: [
            "- Shacman models (H3000, X3000, X6000)",
            "- Engines: Weichai; Gearboxes: FAST",
            "- Trailers use FUWA, CIMC, and BPW axles",
            "- Flatbeds carry up to 32 tons",
            "- Mechanical suspension for dry cargo; Air suspension for liquids/fragile cargo"
        ]
    },
    {
        question: "3. WARRANTY & QUALITY ASSURANCE",
        answer: [
            "- Shacman Trucks: 2 years",
            "- Tippers: 1 year",
            "- Trailers: 6 months",
            "- USD 2 million monthly spare parts stock at Nyerere Road workshop"
        ]
    },
    {
        question: "4. ORDERING & CUSTOMIZATION",
        answer: [
            "- Custom company colors available for China orders (small extra cost)",
            "- Customization adds 3–7 days",
            "- Delivery: Trucks/Flatbeds 30–60 days; Tankers 30–75 days; China stock 30 days"
        ]
    },
    {
        question: "5. LOCAL STOCK & DELIVERY",
        answer: [
            "- Local stock available",
            "- Registration & delivery in 2–4 days",
            "- Clients may inspect vehicles at the bond"
        ]
    },
    {
        question: "6. PAYMENT & FINANCING OPTIONS",
        answer: [
            "Accepted payment: Cash, LC, Bank Financing, Financing Institutions",
            "Banks: CRDB, NBC, NMB, NCBA, BOA, TCB, Maendeleo Bank, Absa, Stanbic",
            "Financiers: PASS, EFTA",
            "Deposit: Minimum 20%",
            "Financing in USD or TZS; up to 5 years tenure"
        ]
    },
    {
        question: "7. BANK FINANCING REQUIREMENTS",
        answer: [
            "Company Documents: Application letter, Board resolution, Incorporation certificate, MEMARTS, TIN, Business licence, Tax clearance, Annual return",
            "Financial Documents: 3 years audited accounts, Management accounts, 3-year cash flow projections, 12 months bank statements, contracts, vehicle registration cards",
            "Transaction Documents: Proforma Invoice from Automax"
        ]
    },
    {
        question: "8. INTERNAL CREDIT POLICY",
        answer: "Automax does NOT offer internal credit except under rare special circumstances."
    },
    {
        question: "9. AFTER-SALES SUPPORT",
        answer: [
            "- Technical consultation",
            "- Driver orientation",
            "- Spare parts availability",
            "- Linkages to drivers, fuel, insurance, clearing, cargo companies"
        ]
    },
    {
        question: "10. COMPLIANCE & REGULATIONS",
        answer: "All trucks and trailers comply with TRA, TBS, and EAC axle-load standards."
    },
    {
        question: "11. DELIVERY, CLEARANCE & REGISTRATION",
        answer: [
            "- Registration: 2–4 days for local stock",
            "- Required: TIN, ID, licence, insurance, company documents"
        ]
    },
    {
        question: "12. PERFORMANCE ON ROUTES",
        answer: "Shacman trucks perform excellently across East African terrains including Morogoro, Tunduma, Congo, Zambia, Mwanza, and Dodoma."
    },
    {
        question: "13. SECURITY & TRACKING",
        answer: "Automax links clients to insurers and tracking providers for GPS, fleet management, and fuel monitoring."
    }
];

interface FAQProps {
    items?: FAQItem[];
    title?: string;
    subtitle?: string;
    label?: string;
}

const FAQ: React.FC<FAQProps> = ({ items, title, subtitle, label }) => {
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    const toggleFAQ = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    // Use dynamic items if provided, otherwise fall back to legacy hardcoded data
    const useDynamic = items && items.length > 0;
    const sortedItems = useDynamic ? [...items].sort((a, b) => a.order - b.order) : null;

    return (
        <section className="py-24 bg-white relative overflow-hidden">
            {/* Decorative blob */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gray-50 rounded-full mix-blend-multiply filter blur-3xl opacity-30 -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-automax-red/5 rounded-full mix-blend-multiply filter blur-3xl opacity-30 translate-y-1/2 -translate-x-1/2"></div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="text-center mb-16">
                    <span className="text-automax-red font-bold tracking-wider uppercase text-sm mb-2 block">{label || 'Support Center'}</span>
                    <h2 className="font-display text-4xl md:text-5xl font-bold text-gray-900 mb-6">{title || 'Frequently Asked Questions'}</h2>
                    <p className="text-gray-600 text-lg max-w-2xl mx-auto">{subtitle || 'Everything you need to know about our products, financing, and delivery services.'}</p>
                </div>

                <div className="space-y-4">
                    {useDynamic ? (
                        sortedItems!.map((faq, index) => (
                            <div
                                key={faq.id}
                                className={`border rounded-lg overflow-hidden transition-all duration-300 ${openIndex === index ? 'border-automax-red/30 shadow-md bg-white' : 'border-gray-100 hover:border-gray-300 bg-gray-50/50'}`}
                            >
                                <button
                                    onClick={() => toggleFAQ(index)}
                                    className="w-full px-8 py-6 flex items-center justify-between text-left focus:outline-none"
                                >
                                    <span className={`font-display font-bold text-lg ${openIndex === index ? 'text-automax-red' : 'text-gray-900'}`}>
                                        {faq.question}
                                    </span>
                                    <div className={`ml-4 p-1 rounded-full transition-transform duration-300 ${openIndex === index ? 'rotate-180 bg-automax-red/10 text-automax-red' : 'text-gray-400'}`}>
                                        <ChevronDown size={20} />
                                    </div>
                                </button>
                                <div
                                    className={`overflow-hidden transition-all duration-300 ease-in-out ${openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                                >
                                    <div className="px-6 pb-6 pt-0 pl-[4.5rem] text-gray-600 leading-relaxed border-t border-gray-100 mt-2">
                                        <p>{faq.answer}</p>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        defaultFaqs.map((faq, index) => (
                            <div
                                key={index}
                                className={`border rounded-lg overflow-hidden transition-all duration-300 ${openIndex === index ? 'border-automax-red/30 shadow-md bg-white' : 'border-gray-100 hover:border-gray-300 bg-gray-50/50'}`}
                            >
                                <button
                                    onClick={() => toggleFAQ(index)}
                                    className="w-full px-8 py-6 flex items-center justify-between text-left focus:outline-none"
                                >
                                    <span className={`font-display font-bold text-lg ${openIndex === index ? 'text-automax-red' : 'text-gray-900'}`}>
                                        {faq.question.replace(/^\d+\.\s*/, '')}
                                    </span>
                                    <div className={`ml-4 p-1 rounded-full transition-transform duration-300 ${openIndex === index ? 'rotate-180 bg-automax-red/10 text-automax-red' : 'text-gray-400'}`}>
                                        <ChevronDown size={20} />
                                    </div>
                                </button>
                                <div
                                    className={`overflow-hidden transition-all duration-300 ease-in-out ${openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                                >
                                    <div className="px-6 pb-6 pt-0 pl-[4.5rem] text-gray-600 leading-relaxed border-t border-gray-100 mt-2">
                                        {Array.isArray(faq.answer) ? (
                                            <ul className="space-y-2">
                                                {faq.answer.map((item, i) => (
                                                    <li key={i}>{item}</li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p>{faq.answer}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </section>
    );
};

export default FAQ;
